import  { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  AddPermission,
  AddPermissionUser,
  GetOneWorkSpace,
  GetOneWorkSpaceFolderInformation,
  RemovePermission,
  RemovePermissionUser
} from "../../../services/WorkSpaceServices";
import { AlertColor, SelectChangeEvent } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { WorkSpaceUpdate } from '../../../model/WorkSpaceModel';
import KeydriveSnackbar from '../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar';
import { UserPermission } from '../../../model/UserModel';
import PrivilegesStateDefault from './components/PrivilegesStateDefault';
import { User } from '../../../model/OrganizationModel';
import { GetOrganizationGroup, GetOrganizationUser } from '../../../services/OrganizationServices';
import PrivilegesStateAddingUser from './components/PrivilegesStateAddingUser';
import PrivilegeStateEditingUser from './components/PrivilegeStateEditingUser';
import { KeyDriveDialog } from '../../../components/KeydriveComponents/Dialog/KeyDriveDialog';
import { Group, GroupPermission } from "../../../model/GroupModel";
import PrivilegesStateAddingGroups from "./components/PrivilegesStateAddingGroups";
import PrivilegeStateEditingGroup from "./components/PrivilegeStateEditingGroup";
import { FolderInformation } from "../../../model/ItemModel";
import { PermissionTargetType } from "../../../enums/PermissionTargetType";

export type StatePrivilegesTypes =
  | "default"
  | "adding user"
  | "adding group"
  | "editing user"
  | "editing group"
  | "deleting permission";

const WorkSpacePermissions  = () => {
  const { idWorkspace, nameOrg } = useParams();
  const [workspace, setWorkspace] = useState<WorkSpaceUpdate>({} as WorkSpaceUpdate)
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [severity, setSeverity] = useState<AlertColor>("success");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [targetType, setTargetType] = useState<PermissionTargetType>(PermissionTargetType.User);
  const [skip] = useState<number>(0);
  const [limit] = useState<number>(10);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [allUsers, setAllUsers] = useState<UserPermission[]>([]);
  const [allUsersMemory, setAllUsersMemory] = useState<UserPermission[]>([]);
  const [allGroups, setAllGroups] = useState<GroupPermission[]>([]);
  const [allGroupsMemory, setAllGroupsMemory] = useState<GroupPermission[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const tableUserHeaders: string[] = [t("Privileges.Name"),t("Privileges.Email"),t("Privileges.Phone"),t("Privileges.Privilege")]
  const tableGroupHeaders: string[] = [t("Privileges.Name"), t("Privileges.Participants"), t("Privileges.Privilege")]
  const [privilegesState, setPrivilegesState] = useState<{ state: StatePrivilegesTypes, id: string, permissionType: number }>({ state: "default", id: "", permissionType: -1 });
  const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
  const [organizationGroups, setOrganizationGroups] = useState<Group[]>([]);
  const [newUserSearch, setNewUserSearch] = useState<string>("");
  const [newGroupSearch, setNewGroupSearch] = useState<string>("");
  const [permissionType, setPermissionType] = useState<number>(5);
  const [folderInfo, setFolderInfo] = useState<FolderInformation>({} as FolderInformation);
  const [reloadWorkspace, setReloadWorkspace] = useState<boolean>();

  useEffect(() => {
    GetWorkspace();
    GetOrganizationUserByName();
    GetOrganizationGroupAsync();
    GetOneWorkSpaceFolderInformationAsync();
  }, [idWorkspace]);

  useEffect(() => {
    if (reloadWorkspace) {
      GetWorkspace().finally(() => setReloadWorkspace(false));
    }
  }, [reloadWorkspace]);

  useEffect(() => {
    if (workspace?.groups) {
      setAllGroups(workspace.groups);
      setAllGroupsMemory(workspace.groups);
    } 

    if (workspace?.users) {
      setAllUsers(workspace.users);
      setAllUsersMemory(workspace.users);
    }
  }, [idWorkspace, workspace]);

  useEffect(() => {
    if (searchFilter !== "" && targetType === PermissionTargetType.User) {
      let filter = allUsers.filter(user => user.userName.toLowerCase().includes(searchFilter.toLowerCase()));
      setAllUsersMemory(filter);
    } else {
      setAllUsersMemory(allUsers);
    }
  }, [searchFilter, targetType, allUsers]);

  useEffect(() => {
    if (searchFilter !== "" && targetType === PermissionTargetType.Group) {
      let filter = allGroups.filter(group => group.groupName.toLowerCase().includes(searchFilter.toLowerCase()));
      setAllGroupsMemory(filter);
    } else {
      setAllGroupsMemory(allGroups);
    }
  }, [searchFilter, targetType, allGroups]);

  async function GetOrganizationGroupAsync() {
    setLoadingUsers(true);
    try {
      const response = await GetOrganizationGroup(nameOrg!, skip, limit);
      setOrganizationGroups(response);
    } catch (err: any) {
      setMessage(err.response?.data ?? "Erro ao carregar grupos.");
      setSeverity("error");
    } finally {
      setLoadingUsers(false);
    }
  }

  async function GetOrganizationUserByName() {
    setLoadingUsers(true);
    try {
      const response = await GetOrganizationUser(nameOrg!);
      setOrganizationUsers(response);
    } catch (err: any) {
      setMessage(err.response?.data ?? "Erro ao carregar usuários.");
      setSeverity("error");
    } finally {
      setLoadingUsers(false);
    }
  }

  async function GetOneWorkSpaceFolderInformationAsync() {
    setLoadingUsers(true);
    try {
      if (idWorkspace != undefined) {
        const response = await GetOneWorkSpaceFolderInformation(idWorkspace);
        setFolderInfo(response);
      }
    } catch(err: any) {
      setMessage(err.response?.data ?? "Erro ao carregar informações da pasta.");
      setSeverity("error");
    } finally {
      setLoadingUsers(false);
    }
  }

  async function GetWorkspace() {
    setLoadingUsers(true);
    try {
      if (idWorkspace !== undefined) {
        const response = await GetOneWorkSpace(idWorkspace);
        if (!response) {
          setSeverity("error");
          setMessage("Não foi possivel recuperar o workspace.");
          navigate("/");
          return;
        }
        setWorkspace(response);
      }
    } catch (err: any) {
      setMessage(err.response?.data ?? "Erro ao carregar workspace.");
      setSeverity("error");
    } finally {
      setLoadingUsers(false);
    }
  }

  async function AddPermissionAsync() {
    setLoadingUsers(true);
    try {
      await AddPermissionUser({
        idObject: idWorkspace!,
        idUser: newUserSearch,
        permissionType: permissionType
      });
    } catch (err: any) {
      setMessage(err.response?.data ?? "Erro ao adicionar permissão.");
      setSeverity("error");
    } finally {
      setLoadingUsers(false);
      backDefault();
      setReloadWorkspace(true);
    }
  }

  async function DeletePermission() {
    setLoadingUsers(true);
    try {
      await RemovePermissionUser({
        idObject: idWorkspace!,
        idUser: privilegesState.id,
        permissionType: privilegesState.permissionType
      });
    } catch(err: any) {
      setMessage(err.response?.data ?? "Erro ao remover permissão de usuário.");
      setSeverity("error");
    } finally {
      setPrivilegesState({ state: "default", id: "", permissionType: -1 });
      setLoadingUsers(false);
      setReloadWorkspace(true);
      backDefault()
    } 
  }

  async function DeletePermissionGroup() {
    setLoadingUsers(true);
    try {
      await RemovePermission({
        idObject: idWorkspace!,
        idGroup: privilegesState.id,
        permissionType: privilegesState.permissionType
      });
    } catch(err: any) {
      setMessage(err.response?.data ?? "Erro ao remover permissão de grupo.");
      setSeverity("error");
    } finally {
      setPrivilegesState({ state: "default", id: "", permissionType: -1 });
      setLoadingUsers(false);
      setReloadWorkspace(true);
      backDefault();
    } 
  }

  async function AddGroupPermissionAsync() {
    setLoadingUsers(true);
    try {
      await AddPermission({
        permissionType: permissionType!,
        idGroup: newGroupSearch,
        idObject: idWorkspace!
      });
    } catch (err: any) {
      setMessage(err.response?.data ?? "Erro ao adicionar permissão ao grupo.");
      setSeverity("error");
    } finally {
      setLoadingUsers(false);
      backDefault();
      setReloadWorkspace(true);
    }
  }

  function handleUser(e: SelectChangeEvent): void {
    setNewUserSearch(e.target.value);
  }

  function handleGroup(e: SelectChangeEvent): void {
    setNewGroupSearch(e.target.value);
  }

  function backDefault() {
    setPrivilegesState({ state: "default", id: "", permissionType: -1 });
    setNewUserSearch("");
    setPermissionType(5);
    setTargetType(PermissionTargetType.User);
  }

  return (
    <>
      { privilegesState.state == "default" && (
        <PrivilegesStateDefault 
          allUsersMemory={allUsersMemory}
          allGroupsMemory={allGroupsMemory}
          loadingUsers={loadingUsers}
          setSearchFilter={setSearchFilter}
          setTargetType={setTargetType}
          tableUserHeaders={tableUserHeaders}
          tableGroupHeaders={tableGroupHeaders}
          targetType={targetType}
          workspaceName={workspace?.name!}
          setPrivilegesState={setPrivilegesState}
          ownerId={folderInfo?.folderOwnerInformation?.id}
          hasFolderChild={folderInfo?.fatherId !== undefined}
        />
      )}
      { privilegesState.state == "adding user" && (
        <PrivilegesStateAddingUser
          AddPermission={AddPermissionAsync}
          handleUser={handleUser}
          newUserSearch={newUserSearch}
          organizationUsers={organizationUsers}
          setPermissionType={setPermissionType}
          targetType={targetType}
          permissionType={permissionType}
          backBtn={backDefault}
          isItemPage={false}
        />
      ) }
      { privilegesState.state == "editing user" && (
        <PrivilegeStateEditingUser
          editPermission={AddPermissionAsync}
          handleUser={handleUser}
          newUserSearch={newUserSearch}
          organizationUsers={organizationUsers}
          setPermissionType={setPermissionType}
          permissionType={permissionType}
          setNewUserSearch={setNewUserSearch}
          privilegesState={privilegesState}
          userId={newUserSearch}
          backBtn={backDefault}
          isItemPage={false}
        />
      ) }
      { privilegesState.state == "adding group" && (
        <PrivilegesStateAddingGroups
          AddPermission={AddGroupPermissionAsync}
          handleUser={handleGroup}
          newGroupSearch={newGroupSearch}
          organizationGroups={organizationGroups}
          setPermissionType={setPermissionType}
          permissionType={permissionType}
          backBtn={backDefault}
          isItemPage={false}
        />
      ) }
      { privilegesState.state == "editing group" && (
        <PrivilegeStateEditingGroup
          editPermission={AddGroupPermissionAsync}
          handleGroup={handleGroup}
          newGroupSearch={newGroupSearch}
          organizationGroup={organizationGroups}
          setPermissionType={setPermissionType}
          permissionType={permissionType}
          setNewGroupSearch={setNewGroupSearch}
          privilegesState={privilegesState}
          groupId={newGroupSearch}
          backBtn={backDefault}
          isItemPage={false}
        />
      ) }
      <KeyDriveDialog 
        keyDriveDialogContent={{
          dialogTitle: t("Privileges.ExcludeTitle"),
          dialogContent: t("Privileges.ExcludeContent"),
          openDialog: privilegesState.state == "deleting permission",
          dialogIcon: <Delete />,
          handleTogglDialog: () => {}
        }}
        keyDriveDialogButtons={{
          rightButtonText: t("Privileges.Confirm"),
          leftButtonText: t("Privileges.Cancel"),
          rightButtonMethod: () => { targetType == PermissionTargetType.User ? DeletePermission() : DeletePermissionGroup() },
          leftButtonMethod: () => { setPrivilegesState({ id: "", permissionType: -1, state: "default" }) }
        }}
        dialogTitleCenter
      />
      <KeydriveSnackbar 
        message={message}
        open={message != undefined}
        onClose={() => {
          setMessage(undefined);
        }}
        variant={severity}
      />
    </>
  );
};

export default WorkSpacePermissions;
